import React, { useState, useRef, useEffect } from "react";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faDownload,
  faFileCsv,
  faFileAlt,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faArrowLeft,
  faCloudUploadAlt,
  faFileExcel,
  faInfoCircle,
  faTimesCircle,
  faChartLine,
  faUsers,
  faDatabase,
  faClipboardCheck,
  faSync,
  faFileInvoice,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "./addTeachersCSV.css";

const AddTeachersFromCSV = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStats, setUploadStats] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  const handleBack = () => {
    navigate("/Manage-Teachers");
  };

  // Handle drag and drop events
  useEffect(() => {
    const dropZone = dropZoneRef.current;
    if (!dropZone) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dropZone.contains(e.relatedTarget)) {
        setIsDragging(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const selectedFile = files[0];
        if (
          selectedFile.type === "text/csv" ||
          selectedFile.name.endsWith(".csv")
        ) {
          handleFileSelect(selectedFile);
        } else {
          Swal.fire({
            icon: "error",
            title: "Invalid File",
            text: "Please upload a CSV file",
            confirmButtonColor: "#667eea",
          });
        }
      }
    };

    dropZone.addEventListener("dragover", handleDragOver);
    dropZone.addEventListener("dragleave", handleDragLeave);
    dropZone.addEventListener("drop", handleDrop);

    return () => {
      dropZone.removeEventListener("dragover", handleDragOver);
      dropZone.removeEventListener("dragleave", handleDragLeave);
      dropZone.removeEventListener("drop", handleDrop);
    };
  }, []);

  const handleFileSelect = (selectedFile) => {
    if (selectedFile) {
      // Validate file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        Swal.fire({
          icon: "error",
          title: "File Too Large",
          text: "File size must be less than 10MB",
          confirmButtonColor: "#667eea",
        });
        return;
      }

      // Validate file type
      if (
        !selectedFile.type.includes("csv") &&
        !selectedFile.name.endsWith(".csv")
      ) {
        Swal.fire({
          icon: "error",
          title: "Invalid File Type",
          text: "Please upload a CSV file",
          confirmButtonColor: "#667eea",
        });
        return;
      }

      setFile(selectedFile);
      setValidationErrors([]);
      setUploadStats(null);

      // Preview validation
      previewFileValidation(selectedFile);
    }
  };

  const previewFileValidation = async (selectedFile) => {
    setIsValidating(true);

    // Simulate validation (in real app, you'd parse and validate the CSV)
    setTimeout(() => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const lines = content.split("\n");
          const headers = lines[0].split(",");

          // Check for required headers
          const requiredHeaders = [
            "name",
            "cnic",
            "primaryMobile",
            "highestEducation",
          ];
          const missingHeaders = requiredHeaders.filter(
            (h) =>
              !headers.some((header) =>
                header.toLowerCase().includes(h.toLowerCase())
              )
          );

          if (missingHeaders.length > 0) {
            setValidationErrors([
              `Missing required columns: ${missingHeaders.join(", ")}`,
            ]);
          } else if (lines.length - 1 > 0) {
            setValidationErrors([]);
            Swal.fire({
              icon: "success",
              title: "File Validated",
              text: `CSV file contains ${lines.length - 1} teacher records`,
              confirmButtonColor: "#667eea",
            });
          }
        } catch (error) {
          setValidationErrors(["Unable to parse CSV file"]);
        }
        setIsValidating(false);
      };
      reader.readAsText(selectedFile);
    }, 1000);
  };

  const handleFileUpload = (e) => {
    handleFileSelect(e.target.files[0]);
  };

  const handleSubmit = async () => {
    if (!file) {
      Swal.fire({
        icon: "warning",
        title: "No File Selected",
        text: "Please select a CSV file to upload",
        confirmButtonColor: "#667eea",
      });
      return;
    }

    if (validationErrors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Errors",
        html: `
          <div style="text-align: left; padding: 1rem;">
            <h4 style="color: #f56565; margin-bottom: 1rem;">File contains errors:</h4>
            <ul style="color: #718096;">
              ${validationErrors.map((error) => `<li>${error}</li>`).join("")}
            </ul>
          </div>
        `,
        confirmButtonColor: "#667eea",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "https://mhsdev.online/upload-csv-teachers", true);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percentage);
      }
    };

    xhr.onload = () => {
      setIsUploading(false);

      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);

        if (response.success) {
          setUploadStats({
            total: response.total || 0,
            successful: response.successful || 0,
            failed: response.failed || 0,
            duplicates: response.duplicates || 0,
            errors: response.errors || [],
          });

          Swal.fire({
            icon: "success",
            title: "Upload Complete!",
            html: `
              <div style="text-align: center; padding: 1rem;">
                <div style="font-size: 4rem; color: #38a169; margin-bottom: 1rem;">
                  <i class="fas fa-check-circle"></i>
                </div>
                <h3 style="color: #2d3748; margin-bottom: 0.5rem;">${response.successful} Teachers Added</h3>
                <p style="color: #718096;">
                  Successfully uploaded teacher data from CSV file
                </p>
              </div>
            `,
            confirmButtonColor: "#667eea",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload Failed",
            text: response.message || "Failed to process CSV file",
            confirmButtonColor: "#667eea",
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Error",
          text: "Server error occurred during upload",
          confirmButtonColor: "#667eea",
        });
      }

      setUploadProgress(0);
    };

    xhr.onloadstart = () => {
      setIsUploading(true);
      setUploadStats(null);
    };

    xhr.onerror = () => {
      setIsUploading(false);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Unable to connect to server. Please check your connection.",
        confirmButtonColor: "#667eea",
      });
      setUploadProgress(0);
    };

    xhr.send(formData);
  };

  const downloadSampleCSV = () => {
    // Create sample CSV content
    const sampleData = [
      [
        "name",
        "fatherName",
        "cnic",
        "primaryMobile",
        "email",
        "highestEducation",
        "gradeOrCgpa",
        "selectFor",
        "experience",
      ],
      [
        "John Doe",
        "Michael Doe",
        "12345-1234567-1",
        "03001234567",
        "john@school.edu.pk",
        "Master",
        "3.8/4.0",
        "Science Subjects",
        "5",
      ],
      [
        "Jane Smith",
        "Robert Smith",
        "54321-7654321-2",
        "03009876543",
        "jane@school.edu.pk",
        "Bachelor",
        "A+",
        "Arts Subjects",
        "3",
      ],
      [
        "Ahmed Khan",
        "Ali Khan",
        "67890-1234567-3",
        "03005556677",
        "ahmed@school.edu.pk",
        "M-Phil",
        "3.9/4.0",
        "Hybrid",
        "8",
      ],
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([sampleData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "teachers_sample_template.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();

    Swal.fire({
      icon: "success",
      title: "Template Downloaded",
      text: "Sample CSV template has been downloaded",
      confirmButtonColor: "#667eea",
    });
  };

  const downloadInstructions = () => {
    const instructions = `
TEACHER CSV IMPORT INSTRUCTIONS

REQUIRED FORMAT:
- File must be in CSV format (Comma Separated Values)
- First row must contain column headers
- Save file with UTF-8 encoding

REQUIRED COLUMNS:
1. name: Full name of teacher (Required)
2. fatherName: Father's name (Required)
3. cnic: CNIC number in format XXXXX-XXXXXXX-X (Required)
4. primaryMobile: Primary mobile number starting with 03 (Required)
5. email: Email address (Optional)
6. highestEducation: Highest education level (Required)
7. gradeOrCgpa: Grade or CGPA (Optional)
8. selectFor: Subject category - Science/Arts/Hybrid (Required)
9. experience: Years of experience (Optional)
10. address: Complete address (Optional)

EDUCATION LEVELS:
- Matric
- Intermediate
- Bachelor
- Master
- BS-16 Years
- M-Phil
- PhD

SUBJECT CATEGORIES:
- Science Subjects
- Arts Subjects
- Hybrid

VALIDATION RULES:
- CNIC must be unique
- Mobile numbers must be valid Pakistani numbers
- Email addresses must be valid format
- Required fields cannot be empty

FILE LIMITS:
- Maximum file size: 10MB
- Maximum records per file: 1000 teachers
    `;

    const blob = new Blob([instructions], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "teachers_csv_instructions.txt");
    document.body.appendChild(link);
    link.click();
    link.remove();

    Swal.fire({
      icon: "info",
      title: "Instructions Downloaded",
      text: "Complete instructions have been downloaded",
      confirmButtonColor: "#667eea",
    });
  };

  const removeFile = () => {
    setFile(null);
    setValidationErrors([]);
    setUploadStats(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="csv-upload-container">
        {/* Header Section */}
        <div className="csv-header">
          <div className="header-left">
            <button onClick={handleBack} className="back-btn">
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Teachers
            </button>
            <div className="header-title">
              <h1>
                <FontAwesomeIcon icon={faFileCsv} />
                Bulk Teacher Registration
              </h1>
              <p>Upload CSV file to register multiple teachers at once</p>
            </div>
          </div>
          <div className="header-badge">
            <FontAwesomeIcon icon={faChartLine} />
            <span>CSV Import</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="csv-grid">
          {/* Instructions Card */}
          <div className="csv-card instructions-card">
            <div className="card-header">
              <FontAwesomeIcon icon={faInfoCircle} />
              <h3>Import Instructions</h3>
            </div>
            <div className="card-body">
              <div className="instruction-list">
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <FontAwesomeIcon icon={faFileExcel} />
                  </div>
                  <div className="instruction-content">
                    <h4>File Format</h4>
                    <p>
                      Upload CSV files with UTF-8 encoding and proper column
                      headers
                    </p>
                  </div>
                </div>
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <FontAwesomeIcon icon={faClipboardCheck} />
                  </div>
                  <div className="instruction-content">
                    <h4>Required Fields</h4>
                    <p>
                      Ensure all required columns are present with valid data
                    </p>
                  </div>
                </div>
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <FontAwesomeIcon icon={faDatabase} />
                  </div>
                  <div className="instruction-content">
                    <h4>Data Validation</h4>
                    <p>
                      System validates CNIC uniqueness and mobile number format
                    </p>
                  </div>
                </div>
                <div className="instruction-item">
                  <div className="instruction-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div className="instruction-content">
                    <h4>Bulk Processing</h4>
                    <p>Process up to 1000 teacher records in a single upload</p>
                  </div>
                </div>
              </div>

              <div className="download-section">
                <button
                  className="download-btn primary"
                  onClick={downloadSampleCSV}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download Sample Template
                </button>
                <button
                  className="download-btn secondary"
                  onClick={downloadInstructions}
                >
                  <FontAwesomeIcon icon={faFileAlt} />
                  Complete Instructions
                </button>
              </div>

              <div className="requirements-section">
                <h4>Required Columns</h4>
                <div className="requirements-grid">
                  <div className="requirement-item required">Name</div>
                  <div className="requirement-item required">Father Name</div>
                  <div className="requirement-item required">CNIC</div>
                  <div className="requirement-item required">Mobile</div>
                  <div className="requirement-item required">Education</div>
                  <div className="requirement-item required">Subject</div>
                  <div className="requirement-item optional">Email</div>
                  <div className="requirement-item optional">Experience</div>
                  <div className="requirement-item optional">Address</div>
                </div>
                <div className="legend">
                  <span className="legend-item required">Required</span>
                  <span className="legend-item optional">Optional</span>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Card */}
          <div className="csv-card upload-card">
            <div className="card-header">
              <FontAwesomeIcon icon={faCloudUploadAlt} />
              <h3>Upload CSV File</h3>
            </div>
            <div className="card-body">
              {/* Drop Zone */}
              <div
                ref={dropZoneRef}
                className={`drop-zone ${isDragging ? "dragging" : ""} ${
                  file ? "has-file" : ""
                }`}
                onClick={() => !file && fileInputRef.current?.click()}
              >
                {!file ? (
                  <div className="drop-zone-content">
                    <div className="upload-icon-large">
                      <FontAwesomeIcon icon={faUpload} />
                    </div>
                    <h4>Drop CSV file here or click to browse</h4>
                    <p>Supports .csv files up to 10MB</p>
                    {isDragging && (
                      <div className="drag-hint">Release to upload</div>
                    )}
                  </div>
                ) : (
                  <div className="file-preview">
                    <div className="file-icon">
                      <FontAwesomeIcon icon={faFileCsv} />
                    </div>
                    <div className="file-info">
                      <h4>{file.name}</h4>
                      <p>{(file.size / 1024).toFixed(2)} KB</p>
                      <div className="file-actions">
                        <button onClick={removeFile} className="remove-btn">
                          <FontAwesomeIcon icon={faTimesCircle} />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="file-input-hidden"
                />
              </div>

              {/* Validation Status */}
              {isValidating && (
                <div className="validation-status validating">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Validating CSV file...</span>
                </div>
              )}

              {validationErrors.length > 0 && (
                <div className="validation-status error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <div className="error-list">
                    <strong>Validation Errors:</strong>
                    {validationErrors.map((error, index) => (
                      <div key={index} className="error-item">
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="upload-progress">
                  <div className="progress-header">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <div className="progress-hint">
                    <FontAwesomeIcon icon={faSync} spin />
                    <span>Processing teacher records...</span>
                  </div>
                </div>
              )}

              {/* Upload Stats */}
              {uploadStats && (
                <div className="upload-stats">
                  <h4>Upload Summary</h4>
                  <div className="stats-grid">
                    <div className="stat-item success">
                      <div className="stat-value">{uploadStats.successful}</div>
                      <div className="stat-label">Successful</div>
                    </div>
                    <div className="stat-item failed">
                      <div className="stat-value">{uploadStats.failed}</div>
                      <div className="stat-label">Failed</div>
                    </div>
                    <div className="stat-item duplicates">
                      <div className="stat-value">{uploadStats.duplicates}</div>
                      <div className="stat-label">Duplicates</div>
                    </div>
                    <div className="stat-item total">
                      <div className="stat-value">{uploadStats.total}</div>
                      <div className="stat-label">Total Records</div>
                    </div>
                  </div>

                  {uploadStats.errors.length > 0 && (
                    <div className="error-details">
                      <h5>Detailed Errors:</h5>
                      <div className="error-scroll">
                        {uploadStats.errors.map((error, index) => (
                          <div key={index} className="detailed-error">
                            Row {error.row}: {error.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="action-buttons">
                <button
                  className="upload-btn"
                  onClick={handleSubmit}
                  disabled={
                    !file ||
                    isUploading ||
                    isValidating ||
                    validationErrors.length > 0
                  }
                >
                  {isUploading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCloudUploadAlt} />
                      Upload CSV File
                    </>
                  )}
                </button>
                <button
                  className="reset-btn"
                  onClick={removeFile}
                  disabled={!file || isUploading}
                >
                  <FontAwesomeIcon icon={faSync} />
                  Reset
                </button>
              </div>

              {/* Quick Tips */}
              <div className="quick-tips">
                <h5>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  Quick Tips
                </h5>
                <ul>
                  <li>Ensure all required columns are present</li>
                  <li>Check for duplicate CNIC numbers</li>
                  <li>Verify mobile number formats (03XXXXXXXXX)</li>
                  <li>Save file as UTF-8 encoded CSV</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="csv-footer">
          <div className="footer-content">
            <p>
              <FontAwesomeIcon icon={faFileInvoice} />
              <strong>Need help?</strong> Download the sample template for
              reference
            </p>
            <div className="footer-stats">
              <span>Max file size: 10MB</span>
              <span className="divider">•</span>
              <span>Max records: 1000</span>
              <span className="divider">•</span>
              <span>Supported format: CSV</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTeachersFromCSV;
